// src/pages/cmdb/CmdbPage.tsx
// Página principal CMDB QOps (Q-870 D2). 4 tabs: Equipos / Sistemas / Accesos / Bootstrap Tokens.
// D4 (Q-872) extiende esto con dashboard de health real.
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
  Card, CardHeader, CardTitle, CardContent,
  Input, Button, Badge, EmptyState, Spinner, Dialog, DialogContent,
  DialogHeader, DialogTitle, DialogFooter, Label,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  toast,
} from '@/components/ui';
import { Server, Box, KeyRound, Copy, Plus, Activity, RefreshCw, History, ChevronDown, ChevronRight, FileText, ClipboardList } from 'lucide-react';
import { cmdbService } from '@/services/cmdbService';
import type { DeployHealthRow, DeployHealthStatus, AuditRunSummary, ManifestSnapshotResponse } from '@/services/cmdbService';
import { clientesService } from '@/services/clientes';
import { PlanDeployTab } from './PlanDeployTab';
import type {
  Acceso,
  BootstrapToken,
  Equipo,
  SistemaInstalado,
} from '@/types/cmdb';
import type { Cliente } from '@/types';

const SISTEMA_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  qpweb: 'default',
  qgeo: 'secondary',
  qpos: 'outline',
};

function formatDate(s?: string | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

// =============================================================================
// EquiposTab
// =============================================================================

function EquiposTab() {
  const [items, setItems] = useState<Equipo[]>([]);
  const [loading, setLoading] = useState(false);
  const [detalleEquipo, setDetalleEquipo] = useState<Equipo | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cmdbService.listEquipos({ activo: true, page_size: 100 });
      setItems(r.items);
    } catch (e) {
      toast.error(`Error cargando equipos: ${String(e)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center p-8"><Spinner /></div>;
  if (items.length === 0) {
    return (
      <EmptyState
        icon={Server}
        title="No hay equipos registrados"
        description="Los equipos aparecen aquí automáticamente cuando un agente Quantum hace su primer registro contra el hub."
      />
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">Click en un equipo para ver detalle (sistemas + última auditoría).</p>
      <table className="w-full text-sm">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            <th className="p-2 text-left">Hostname</th>
            <th className="p-2 text-left">Cliente</th>
            <th className="p-2 text-left">Tipo</th>
            <th className="p-2 text-left">IP LAN</th>
            <th className="p-2 text-left">IP Tailscale</th>
            <th className="p-2 text-left">OS</th>
            <th className="p-2 text-left">Activo</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr
              key={e.id}
              className="border-b cursor-pointer hover:bg-muted/40"
              onClick={() => setDetalleEquipo(e)}
              data-testid={`equipo-row-${e.hostname}`}
            >
              <td className="p-2 font-medium">{e.hostname}</td>
              <td className="p-2">{e.cliente_nombre ?? '—'}</td>
              <td className="p-2"><Badge variant="outline">{e.tipo}</Badge></td>
              <td className="p-2">{e.ip_lan ?? '—'}</td>
              <td className="p-2">{e.ip_tailscale ?? '—'}</td>
              <td className="p-2">{e.os ?? '—'}</td>
              <td className="p-2">
                <Badge variant={e.activo ? 'default' : 'outline'}>{e.activo ? 'Sí' : 'No'}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <EquipoDetalleDialog
        equipo={detalleEquipo}
        onClose={() => setDetalleEquipo(null)}
      />
    </div>
  );
}

function EquipoDetalleDialog({ equipo, onClose }: { equipo: Equipo | null; onClose: () => void }) {
  const [sistemas, setSistemas] = useState<SistemaInstalado[]>([]);
  const [runs, setRuns] = useState<AuditRunSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const open = equipo !== null;

  useEffect(() => {
    if (!equipo) {
      setSistemas([]);
      setRuns([]);
      return;
    }
    setLoading(true);
    Promise.all([
      cmdbService.listSistemas({ equipo_id: equipo.id, page_size: 50 }),
      cmdbService.listAuditRuns(5),
    ])
      .then(([sis, runs]) => {
        setSistemas(sis.items);
        setRuns(runs);
      })
      .catch((e) => toast.error(`Error cargando detalle: ${String(e)}`))
      .finally(() => setLoading(false));
  }, [equipo]);

  if (!equipo) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{equipo.hostname}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground">Cliente:</span> <span className="font-medium">{equipo.cliente_nombre ?? '—'}</span></div>
            <div><span className="text-muted-foreground">Tipo:</span> <Badge variant="outline">{equipo.tipo}</Badge></div>
            <div><span className="text-muted-foreground">IP LAN:</span> <span className="font-mono">{equipo.ip_lan ?? '—'}</span></div>
            <div><span className="text-muted-foreground">IP Tailscale:</span> <span className="font-mono">{equipo.ip_tailscale ?? '—'}</span></div>
            <div><span className="text-muted-foreground">OS:</span> {equipo.os ?? '—'}</div>
            <div><span className="text-muted-foreground">Activo:</span> <Badge variant={equipo.activo ? 'default' : 'outline'}>{equipo.activo ? 'Sí' : 'No'}</Badge></div>
            <div><span className="text-muted-foreground">Registrado:</span> {formatDate(equipo.created_at)}</div>
            <div><span className="text-muted-foreground">Actualizado:</span> {formatDate(equipo.updated_at)}</div>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold">Sistemas instalados</h4>
            {loading ? (
              <Spinner />
            ) : sistemas.length === 0 ? (
              <p className="text-xs text-muted-foreground">El agente no ha reportado sistemas instalados todavía.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Sistema</th>
                    <th className="p-2 text-left">Versión</th>
                    <th className="p-2 text-left">Stack</th>
                    <th className="p-2 text-left">Ambiente</th>
                    <th className="p-2 text-left">Manifest hash</th>
                    <th className="p-2 text-left">Estado</th>
                    <th className="p-2 text-left">Último audit</th>
                  </tr>
                </thead>
                <tbody>
                  {sistemas.map((s) => (
                    <tr key={s.id} className="border-b">
                      <td className="p-2"><Badge variant={SISTEMA_BADGE_VARIANT[s.sistema] ?? 'outline'}>{s.sistema}</Badge></td>
                      <td className="p-2 font-mono">{s.version_instalada ?? '—'}</td>
                      <td className="p-2 text-muted-foreground">{s.target_stack ?? '—'}</td>
                      <td className="p-2">{s.ambiente_nombre ?? '—'}</td>
                      <td className="p-2 font-mono text-muted-foreground">{s.manifest_hash ? `${s.manifest_hash.slice(0, 8)}…` : '—'}</td>
                      <td className="p-2"><Badge variant={s.estado === 'ACTIVO' ? 'default' : 'outline'}>{s.estado}</Badge></td>
                      <td className="p-2 text-muted-foreground">{formatDate(s.ultimo_audit_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold">Últimas auditorías (todo el ecosistema)</h4>
            {runs.length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin auditorías aún. Ejecuta "Auditar ahora" desde el tab Estado Deploy.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Estado</th>
                    <th className="p-2 text-right">Match</th>
                    <th className="p-2 text-right">Gap</th>
                    <th className="p-2 text-right">Drift</th>
                    <th className="p-2 text-right">Errores</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-b">
                      <td className="p-2 font-mono">{formatDate(r.finished_at ?? r.started_at)}</td>
                      <td className="p-2"><Badge variant={AUDIT_STATUS_VARIANT[r.status] ?? 'outline'}>{r.status}</Badge></td>
                      <td className="p-2 text-right">{r.matches ?? 0}</td>
                      <td className="p-2 text-right">{r.gaps_found ?? 0}</td>
                      <td className="p-2 text-right">{r.drifts_found ?? 0}</td>
                      <td className="p-2 text-right">{r.errors ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// SistemasTab
// =============================================================================

function SistemasTab() {
  const [items, setItems] = useState<SistemaInstalado[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cmdbService.listSistemas({ page_size: 100 });
      setItems(r.items);
    } catch (e) {
      toast.error(`Error cargando sistemas: ${String(e)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center p-8"><Spinner /></div>;
  if (items.length === 0) {
    return (
      <EmptyState
        icon={Box}
        title="No hay sistemas instalados registrados"
        description="Los sistemas aparecen aquí cuando un agente Quantum reporta qué proyectos corren en el equipo del cliente."
      />
    );
  }

  return (
    <table className="w-full text-sm">
      <thead className="bg-muted text-muted-foreground">
        <tr>
          <th className="p-2 text-left">Sistema</th>
          <th className="p-2 text-left">Versión</th>
          <th className="p-2 text-left">Stack</th>
          <th className="p-2 text-left">Equipo</th>
          <th className="p-2 text-left">Ambiente</th>
          <th className="p-2 text-left">Empresa</th>
          <th className="p-2 text-left">Estado</th>
          <th className="p-2 text-left">Último audit</th>
        </tr>
      </thead>
      <tbody>
        {items.map((s) => (
          <tr key={s.id} className="border-b">
            <td className="p-2">
              <Badge variant={SISTEMA_BADGE_VARIANT[s.sistema] ?? 'outline'}>{s.sistema}</Badge>
            </td>
            <td className="p-2 font-mono text-xs">{s.version_instalada ?? '—'}</td>
            <td className="p-2 text-muted-foreground">{s.target_stack ?? '—'}</td>
            <td className="p-2">{s.equipo_hostname ?? '—'}</td>
            <td className="p-2">{s.ambiente_nombre ?? '—'}</td>
            <td className="p-2">{s.empresa_nombre ?? '—'}</td>
            <td className="p-2">
              <Badge variant={s.estado === 'ACTIVO' ? 'default' : 'outline'}>{s.estado}</Badge>
            </td>
            <td className="p-2 text-xs text-muted-foreground">{formatDate(s.ultimo_audit_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// =============================================================================
// EstadoDeployTab (Q-912) — matriz cliente x sistema x artifact
// =============================================================================

const DEPLOY_STATUS_VARIANT: Record<DeployHealthStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  match: 'default',
  gap: 'destructive',
  drift: 'destructive',
  unknown: 'outline',
  rogue: 'secondary',
};

const DEPLOY_STATUS_LABEL: Record<DeployHealthStatus, string> = {
  match: '✓ Match',
  gap: '⚠ Gap',
  drift: '⚠ Drift',
  unknown: '? Unknown',
  rogue: '◇ Rogue',
};

const SISTEMAS_DISPONIBLES = ['qpweb', 'qgeo', 'qpos', 'qfield'];

function shortHash(checksum?: string | null): string {
  if (!checksum) return '—';
  const clean = checksum.replace(/^sha256:/, '');
  return clean.length > 12 ? `${clean.slice(0, 8)}...${clean.slice(-4)}` : clean;
}

const AUDIT_STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  completed: 'default',
  running: 'outline',
  failed: 'destructive',
};

function EstadoDeployTab() {
  const [rows, setRows] = useState<DeployHealthRow[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterClienteId, setFilterClienteId] = useState<string>('');
  const [filterSistema, setFilterSistema] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [lastRun, setLastRun] = useState<AuditRunSummary | null>(null);
  const [historial, setHistorial] = useState<AuditRunSummary[]>([]);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [auditing, setAuditing] = useState(false);

  // Q-914: cleanup tracking — unmount aborta polling + invalida loads stale.
  const unmountedRef = useRef(false);
  // Token incrementado por load() — descarta responses de cargas previas
  // cuando filtros cambian rapido (race condition: load N puede llegar
  // despues de load N+1, mostrando datos viejos).
  const loadTokenRef = useRef(0);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const load = useCallback(async () => {
    const token = ++loadTokenRef.current;
    setLoading(true);
    try {
      const [data, runs] = await Promise.all([
        cmdbService.listDeployHealth({
          cliente_id: filterClienteId || undefined,
          sistema: filterSistema || undefined,
          status: (filterStatus || undefined) as DeployHealthStatus | undefined,
          limit: 500,
        }),
        cmdbService.listAuditRuns(20),
      ]);
      // Q-914 race fix: si hubo un load mas reciente, descartar este response.
      if (unmountedRef.current || token !== loadTokenRef.current) return;
      setRows(data);
      setHistorial(runs);
      setLastRun(runs[0] ?? null);
    } catch (e) {
      if (unmountedRef.current || token !== loadTokenRef.current) return;
      toast.error(`Error cargando matriz deploy: ${String(e)}`);
    } finally {
      if (!unmountedRef.current && token === loadTokenRef.current) {
        setLoading(false);
      }
    }
  }, [filterClienteId, filterSistema, filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    clientesService.list({ page_size: 200 })
      .then((r) => {
        if (unmountedRef.current) return;
        setClientes(r.items);
      })
      .catch((e) => {
        if (unmountedRef.current) return;
        toast.error(`Error cargando clientes: ${String(e)}`);
      });
  }, []);

  const handleAuditar = useCallback(async () => {
    setAuditing(true);
    try {
      const r = await cmdbService.triggerAuditRun();
      const runId = r.run_id;
      if (unmountedRef.current) return;
      toast.success(`Auditoría iniciada (run ${runId.slice(0, 8)})`);

      // Poll cada 3s hasta status != "running" (max 60s).
      // Q-914 cleanup: si user navega fuera del tab durante polling, abortar.
      const startedPolling = Date.now();
      const POLL_MAX_MS = 60_000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 3_000));
        if (unmountedRef.current) return;
        const run = await cmdbService.getAuditRun(runId);
        if (unmountedRef.current) return;
        if (run.status !== 'running') {
          setLastRun(run);
          toast.success(`Auditoría ${run.status} — gaps ${run.gaps_found ?? 0}, drifts ${run.drifts_found ?? 0}`);
          await load();
          break;
        }
        if (Date.now() - startedPolling > POLL_MAX_MS) {
          toast.error('Auditoría tomó >60s — revisar logs del hub');
          break;
        }
      }
    } catch (e) {
      if (unmountedRef.current) return;
      toast.error(`Error en auditoría: ${String(e)}`);
    } finally {
      if (!unmountedRef.current) {
        setAuditing(false);
      }
    }
  }, [load]);

  const clienteNombrePorId = new Map(clientes.map((c) => [c.id, c.nombre]));

  return (
    <div className="space-y-4" data-testid="estado-deploy-tab">
      <div className="flex flex-wrap items-end gap-3 p-3 border rounded bg-muted/30">
        <div className="space-y-1">
          <Label className="text-xs">Cliente</Label>
          <Select value={filterClienteId || 'all'} onValueChange={(v) => setFilterClienteId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {clientes.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Sistema</Label>
          <Select value={filterSistema || 'all'} onValueChange={(v) => setFilterSistema(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {SISTEMAS_DISPONIBLES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Estado</Label>
          <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="match">Match</SelectItem>
              <SelectItem value="gap">Gap</SelectItem>
              <SelectItem value="drift">Drift</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
              <SelectItem value="rogue">Rogue</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button onClick={handleAuditar} disabled={auditing} data-testid="btn-auditar-ahora">
          <RefreshCw className={`h-4 w-4 mr-1 ${auditing ? 'animate-spin' : ''}`} />
          {auditing ? 'Auditando...' : 'Auditar ahora'}
        </Button>
      </div>

      {lastRun && (
        <div className="text-xs text-muted-foreground" data-testid="ultima-auditoria">
          Última auditoría: {formatDate(lastRun.finished_at ?? lastRun.started_at)} ·
          {' '}{lastRun.matches ?? 0} match · {lastRun.gaps_found ?? 0} gap · {lastRun.drifts_found ?? 0} drift ·
          {' '}{lastRun.rogues_found ?? 0} rogue · {lastRun.agentes_unreachable ?? 0} unreachable
        </div>
      )}

      {historial.length > 0 && (
        <div className="border rounded" data-testid="historial-auditorias">
          <button
            type="button"
            onClick={() => setHistorialOpen((v) => !v)}
            className="flex w-full items-center gap-2 p-2 text-sm font-medium hover:bg-muted/50"
          >
            {historialOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <History className="h-4 w-4" />
            Historial de auditorías ({historial.length})
          </button>
          {historialOpen && (
            <div className="overflow-x-auto border-t">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Estado</th>
                    <th className="p-2 text-left">Origen</th>
                    <th className="p-2 text-right">Equipos</th>
                    <th className="p-2 text-right">Match</th>
                    <th className="p-2 text-right">Gap</th>
                    <th className="p-2 text-right">Drift</th>
                    <th className="p-2 text-right">Rogue</th>
                    <th className="p-2 text-right">Unreach</th>
                    <th className="p-2 text-right">Errores</th>
                    <th className="p-2 text-left">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((r) => {
                    const hasErrors = (r.errors ?? 0) > 0 || (r.agentes_unreachable ?? 0) > 0;
                    const errLog = r.error_log;
                    const firstErr = errLog ? Object.values(errLog)[0] : null;
                    return (
                      <tr key={r.id} className="border-b">
                        <td className="p-2 font-mono">{formatDate(r.finished_at ?? r.started_at)}</td>
                        <td className="p-2">
                          <Badge variant={AUDIT_STATUS_VARIANT[r.status] ?? 'outline'}>{r.status}</Badge>
                        </td>
                        <td className="p-2 text-muted-foreground">{r.trigger_source ?? '—'}</td>
                        <td className="p-2 text-right">{r.agentes_total ?? 0}</td>
                        <td className="p-2 text-right text-green-700 dark:text-green-400">{r.matches ?? 0}</td>
                        <td className="p-2 text-right text-amber-700 dark:text-amber-400">{r.gaps_found ?? 0}</td>
                        <td className="p-2 text-right text-amber-700 dark:text-amber-400">{r.drifts_found ?? 0}</td>
                        <td className="p-2 text-right text-muted-foreground">{r.rogues_found ?? 0}</td>
                        <td className="p-2 text-right text-muted-foreground">{r.agentes_unreachable ?? 0}</td>
                        <td className="p-2 text-right">{r.errors ?? 0}</td>
                        <td className="p-2 text-muted-foreground truncate max-w-md" title={typeof firstErr === 'string' ? firstErr : ''}>
                          {hasErrors && firstErr ? String(firstErr).slice(0, 60) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Sin datos de deploy_health"
          description="Ejecuta 'Auditar ahora' para poblar la matriz. Si el agente no tiene ip_tailscale, ningún artifact se observará."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="p-2 text-left">Cliente</th>
                <th className="p-2 text-left">Sistema</th>
                <th className="p-2 text-left">BD</th>
                <th className="p-2 text-left">Artifact</th>
                <th className="p-2 text-left">Esperado</th>
                <th className="p-2 text-left">Aplicado</th>
                <th className="p-2 text-left">Estado</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b" data-testid={`row-${r.artifact_id}`}>
                  <td className="p-2">{clienteNombrePorId.get(r.cliente_id) ?? r.cliente_id.slice(0, 8)}</td>
                  <td className="p-2">
                    <Badge variant={SISTEMA_BADGE_VARIANT[r.sistema] ?? 'outline'}>{r.sistema}</Badge>
                  </td>
                  <td className="p-2 text-xs text-muted-foreground">{r.bd ?? '—'}</td>
                  <td className="p-2 font-mono text-xs">{r.artifact_id}</td>
                  <td className="p-2 font-mono text-xs">
                    {r.version_expected} · {shortHash(r.checksum_expected)}
                  </td>
                  <td className="p-2 font-mono text-xs">
                    {r.version_applied ? `${r.version_applied} · ${shortHash(r.checksum_observed)}` : '—'}
                  </td>
                  <td className="p-2">
                    <Badge variant={DEPLOY_STATUS_VARIANT[r.status]}>{DEPLOY_STATUS_LABEL[r.status]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ManifiestosTab (Q-918) — viewer del manifest declarado por sistema
// =============================================================================

const SISTEMAS_DISPONIBLES_MANIFEST = ['qpweb', 'qgeo', 'qpos', 'qfield', 'qcollab', 'qfleet', 'erp_gateway'];

function ManifiestosTab() {
  const [sistema, setSistema] = useState<string>('qpweb');
  const [snapshot, setSnapshot] = useState<ManifestSnapshotResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await cmdbService.getManifest(sistema);
      if (unmountedRef.current) return;
      setSnapshot(data);
    } catch (e) {
      if (unmountedRef.current) return;
      const msg = (e as Error).message ?? String(e);
      setSnapshot(null);
      // 404 -> sin snapshot todavia; otro error -> mensaje real
      setErrorMsg(msg.includes('404') ? 'Sin manifest_snapshot ok — ejecutar Auditar ahora primero' : msg);
    } finally {
      if (!unmountedRef.current) setLoading(false);
    }
  }, [sistema]);

  useEffect(() => { load(); }, [load]);

  // Stats: count por phase + count por type
  const stats = (() => {
    if (!snapshot) return null;
    const byPhase: Record<string, number> = {};
    const byType: Record<string, number> = {};
    for (const a of snapshot.artifacts) {
      const phase = a.phase ?? 'sin_fase';
      byPhase[phase] = (byPhase[phase] || 0) + 1;
      const type = a.type ?? 'sin_type';
      byType[type] = (byType[type] || 0) + 1;
    }
    return { byPhase, byType, total: snapshot.artifacts.length };
  })();

  // Group artifacts by phase manteniendo orden del manifest.phases
  const grouped = (() => {
    const map = new Map<string, ManifestSnapshotResponse['artifacts']>();
    if (!snapshot) return map;
    const phasesInOrder = snapshot.phases.length > 0 ? snapshot.phases : Array.from(new Set(snapshot.artifacts.map((a) => a.phase ?? '_'))) ;
    for (const p of phasesInOrder) map.set(p, []);
    for (const a of snapshot.artifacts) {
      const k = a.phase ?? '_';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(a);
    }
    return map;
  })();

  return (
    <div className="space-y-4" data-testid="manifiestos-tab">
      <div className="flex flex-wrap items-end gap-3 p-3 border rounded bg-muted/30">
        <div className="space-y-1">
          <Label className="text-xs">Sistema</Label>
          <Select value={sistema} onValueChange={setSistema}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SISTEMAS_DISPONIBLES_MANIFEST.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Recargar
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : errorMsg ? (
        <EmptyState
          icon={FileText}
          title={errorMsg.includes('Sin manifest') ? 'Sin manifest aún' : 'Error cargando manifest'}
          description={errorMsg}
        />
      ) : snapshot ? (
        <div className="space-y-3">
          <div className="text-xs space-y-1 text-muted-foreground">
            <div>
              <span className="font-mono">{snapshot.sistema}</span> ·
              versión <span className="font-mono">{snapshot.version ?? '—'}</span> ·
              stack <span className="font-mono">{snapshot.target_stack ?? '—'}</span> ·
              source <span className="font-mono">{snapshot.source_ref}</span>
            </div>
            <div>
              Hash <span className="font-mono">{snapshot.manifest_hash.slice(0, 12)}...</span> ·
              Fetched {formatDate(snapshot.fetched_at)}
            </div>
            {stats && (
              <div className="flex flex-wrap gap-3 pt-1">
                <Badge variant="default">Total: {stats.total}</Badge>
                {Object.entries(stats.byPhase).map(([p, n]) => (
                  <Badge key={`p-${p}`} variant="outline">{p}: {n}</Badge>
                ))}
                {Object.entries(stats.byType).slice(0, 6).map(([t, n]) => (
                  <Badge key={`t-${t}`} variant="secondary">{t}: {n}</Badge>
                ))}
              </div>
            )}
          </div>

          {[...grouped.entries()].map(([phase, artifacts]) => artifacts.length > 0 && (
            <div key={phase} className="border rounded">
              <div className="p-2 bg-muted/50 font-medium text-sm">
                {phase} <span className="text-muted-foreground text-xs">({artifacts.length})</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/30 text-muted-foreground">
                    <tr>
                      <th className="p-2 text-left">Artifact ID</th>
                      <th className="p-2 text-left">Type</th>
                      <th className="p-2 text-left">Version</th>
                      <th className="p-2 text-left">Checksum</th>
                      <th className="p-2 text-left">Scope</th>
                      <th className="p-2 text-left">Empresa</th>
                      <th className="p-2 text-left">Path</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifacts.map((a, i) => (
                      <tr key={`${a.artifact_id}-${i}`} className="border-b">
                        <td className="p-2 font-mono">{a.artifact_id}</td>
                        <td className="p-2"><Badge variant="outline">{a.type ?? '—'}</Badge></td>
                        <td className="p-2 font-mono">{a.version ?? a.since_version ?? '—'}</td>
                        <td className="p-2 font-mono text-muted-foreground">{shortHash(a.checksum_sha256)}</td>
                        <td className="p-2 text-muted-foreground">{a.scope ?? '—'}</td>
                        <td className="p-2 text-muted-foreground">{a.required_for_empresa ?? '—'}</td>
                        <td className="p-2 font-mono text-muted-foreground truncate max-w-xs" title={a.path ?? ''}>{a.path ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// =============================================================================
// AccesosTab
// =============================================================================

function AccesosTab() {
  const [items, setItems] = useState<Acceso[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cmdbService.listAccesos({
        search: search.trim() || undefined,
        page_size: 100,
      });
      setItems(r.items);
    } catch (e) {
      toast.error(`Error cargando accesos: ${String(e)}`);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-3">
      <Input
        placeholder="Buscar por alias, host o nota..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />
      {loading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No hay accesos"
          description="Registra accesos SSH/RDP/Tailscale para tener un buscador rápido cross-cliente."
        />
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="p-2 text-left">Alias</th>
              <th className="p-2 text-left">Tipo</th>
              <th className="p-2 text-left">Conexión</th>
              <th className="p-2 text-left">Target</th>
              <th className="p-2 text-left">Notas</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b">
                <td className="p-2 font-medium">{a.alias}</td>
                <td className="p-2"><Badge variant="outline">{a.tipo}</Badge></td>
                <td className="p-2 font-mono text-xs">{a.connection_string}</td>
                <td className="p-2">{a.target_equipo_hostname ?? a.target_sistema_label ?? '—'}</td>
                <td className="p-2 text-muted-foreground">{a.notas ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// =============================================================================
// BootstrapTokensTab (admin)
// =============================================================================

function BootstrapTokensTab() {
  const [items, setItems] = useState<BootstrapToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteId, setClienteId] = useState<string>('');
  const [ttlHours, setTtlHours] = useState<number>(72);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await cmdbService.listBootstrapTokens();
      setItems(r.items);
    } catch (e) {
      toast.error(`Error cargando bootstrap tokens: ${String(e)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = useCallback(async () => {
    setCreatedSecret(null);
    setClienteId('');
    setTtlHours(72);
    setOpen(true);
    try {
      const r = await clientesService.list({ page_size: 200 });
      setClientes(r.items);
    } catch (e) {
      toast.error(`Error cargando clientes: ${String(e)}`);
    }
  }, []);

  const handleCreate = useCallback(async () => {
    if (!clienteId) return;
    setCreating(true);
    try {
      const r = await cmdbService.createBootstrapToken(clienteId, ttlHours);
      setCreatedSecret(r.plain_secret);
      await load();
    } catch (e) {
      toast.error(`Error creando token: ${String(e)}`);
    } finally {
      setCreating(false);
    }
  }, [clienteId, ttlHours, load]);

  const handleCopy = useCallback(() => {
    if (createdSecret) {
      navigator.clipboard.writeText(createdSecret);
      toast.success('Bootstrap secret copiado al portapapeles');
    }
  }, [createdSecret]);

  const handleRevoke = useCallback(async (id: string) => {
    if (!confirm('¿Revocar este bootstrap token? No se podrá usar para registrar un agente.')) return;
    try {
      await cmdbService.revokeBootstrapToken(id);
      await load();
      toast.success('Token revocado');
    } catch (e) {
      toast.error(`Error revocando token: ${String(e)}`);
    }
  }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Tokens single-use para configurar un agente nuevo. El plaintext se muestra UNA sola vez al crear.
        </p>
        <Button onClick={openCreate} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Generar bootstrap
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No hay bootstrap tokens"
          description="Genera uno para registrar un nuevo agente Quantum en un cliente."
        />
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="p-2 text-left">Cliente</th>
              <th className="p-2 text-left">Creado</th>
              <th className="p-2 text-left">Expira</th>
              <th className="p-2 text-left">Estado</th>
              <th className="p-2 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => {
              const expired = new Date(t.expires_at) < new Date();
              const status = t.deleted_at
                ? 'Revocado'
                : t.used_at
                  ? 'Usado'
                  : expired
                    ? 'Expirado'
                    : 'Activo';
              const variant = status === 'Activo' ? 'default' : 'outline';
              return (
                <tr key={t.id} className="border-b">
                  <td className="p-2">{t.cliente_nombre ?? t.cliente_id}</td>
                  <td className="p-2 text-xs">{formatDate(t.created_at)}</td>
                  <td className="p-2 text-xs">{formatDate(t.expires_at)}</td>
                  <td className="p-2"><Badge variant={variant}>{status}</Badge></td>
                  <td className="p-2">
                    {status === 'Activo' && (
                      <Button size="sm" variant="outline" onClick={() => handleRevoke(t.id)}>
                        Revocar
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generar bootstrap token</DialogTitle>
          </DialogHeader>
          {createdSecret ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Copia el secret ahora — NO se podrá ver de nuevo:
              </p>
              <div className="flex items-center gap-2 p-2 border rounded bg-muted">
                <code className="flex-1 text-xs break-all">{createdSecret}</code>
                <Button size="sm" variant="outline" onClick={handleCopy}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Guardalo en <code>/etc/quantum-agent/bootstrap.env</code> del cliente y reinicia el agente.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Cliente</Label>
                <Select value={clienteId} onValueChange={setClienteId}>
                  <SelectTrigger><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                  <SelectContent>
                    {clientes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>TTL (horas)</Label>
                <Input
                  type="number"
                  min={1}
                  max={720}
                  value={ttlHours}
                  onChange={(e) => setTtlHours(parseInt(e.target.value || '72', 10))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            {createdSecret ? (
              <Button onClick={() => setOpen(false)}>Cerrar</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={handleCreate} disabled={!clienteId || creating}>
                  {creating ? 'Generando...' : 'Generar'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// CmdbPage
// =============================================================================

export function CmdbPage() {
  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-2xl font-bold">CMDB — Infraestructura Quantum</h1>
        <p className="text-sm text-muted-foreground">
          Equipos, sistemas instalados y accesos del ecosistema Quantum. Auto-poblado por los agentes Quantum (Q-869).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Inventario</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="plan-deploy">
            <TabsList>
              <TabsTrigger value="plan-deploy" data-testid="tab-plan-deploy">
                <ClipboardList className="h-4 w-4 mr-1" /> Plan de Deploy
              </TabsTrigger>
              <TabsTrigger value="estado-deploy" data-testid="tab-estado-deploy">
                <Activity className="h-4 w-4 mr-1" /> Estado Deploy
              </TabsTrigger>
              <TabsTrigger value="manifiestos" data-testid="tab-manifiestos">
                <FileText className="h-4 w-4 mr-1" /> Manifiestos
              </TabsTrigger>
              <TabsTrigger value="equipos">
                <Server className="h-4 w-4 mr-1" /> Equipos
              </TabsTrigger>
              <TabsTrigger value="sistemas">
                <Box className="h-4 w-4 mr-1" /> Sistemas
              </TabsTrigger>
              <TabsTrigger value="accesos">
                <KeyRound className="h-4 w-4 mr-1" /> Accesos
              </TabsTrigger>
              <TabsTrigger value="bootstrap">
                <Plus className="h-4 w-4 mr-1" /> Bootstrap
              </TabsTrigger>
            </TabsList>

            <TabsContent value="plan-deploy"><PlanDeployTab /></TabsContent>
            <TabsContent value="estado-deploy"><EstadoDeployTab /></TabsContent>
            <TabsContent value="manifiestos"><ManifiestosTab /></TabsContent>
            <TabsContent value="equipos"><EquiposTab /></TabsContent>
            <TabsContent value="sistemas"><SistemasTab /></TabsContent>
            <TabsContent value="accesos"><AccesosTab /></TabsContent>
            <TabsContent value="bootstrap"><BootstrapTokensTab /></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
